import React, { useEffect, useRef, useState } from 'react';
import { Form } from 'formik-antd';
import { Button, Col, Divider, Modal, Row } from 'antd';
import { Formik, FormikProps } from 'formik';
import { toNumber } from 'lodash';
import cx from 'classnames';

import {
  EditEntityInfoFormValues,
  EditEntityInfoSettings,
  getEditEntityInfoSettings,
  isEinOption,
  isSsnOption,
} from 'utils/entityUtils';
import {
  BOUserTypeEnum,
  EntityInfoNumberTypeEnum,
  EntityInformationRequestModel,
  EntityInformationResponseModel,
  ResourceDownloadResponse,
} from 'api/backofficeApiClient';
import W8W9Uploder from './W8W9Uploder';
import Radio from 'components/formik/Radio';
import BaseAlert from 'components/wrappers/BaseAlert';
import SubmitButton from 'components/formik/SubmitButton';
import FormItem from 'components/formik/FormItem';
import MaskedInput from 'components/formik/MaskedInput';
import Input from 'components/formik/Input';
import { einMask, ssnMask } from 'utils/validationUtils';
import { ApiErrorMessage } from 'constants/ErrorMessages';
import { getAllCountries } from 'redux/reducers/regionSlice';
import {
  countriesSelector,
  isCountriesLoadingSelector,
} from 'redux/selectors/regionSelectors';
import { updateEntityInformation } from 'redux/reducers/accountSlice';
import { BaseModal, BaseModalFooter } from 'components/wrappers/BaseModal';
import { isAdminSelector } from 'redux/selectors/authenticationSelectors';
import FormikSelectWithLoading from 'hocs/withLoading/FormikSelectWithLoading';
import { updateRecruiterEntityInformation } from 'redux/reducers/recruiterSlice';
import { useAppDispatch } from 'hooks/useAppDispatch';
import { useAppSelector } from 'hooks/useAppSelector';

import styles from './EditEntityInfoModal.module.less';

type Props = {
  companyId?: string;
  onClose: () => void;
  onEdit: () => Promise<void>;
  userType?: BOUserTypeEnum;
  entityInfoFile?: ResourceDownloadResponse;
  entityInfo?: EntityInformationResponseModel;
};

export default function EditEntityInfoModal(props: Props) {
  const { companyId, onEdit, onClose, userType, entityInfo, entityInfoFile } =
    props;
  const dispatch = useAppDispatch();
  const formRef: any = useRef();
  const isAdmin = useAppSelector(isAdminSelector);
  const countries = useAppSelector(countriesSelector);
  const isCountriesLoading = useAppSelector(isCountriesLoadingSelector);
  const [editEntityInfoSettings, setEditEntityInfoSettings] =
    useState<EditEntityInfoSettings>();
  const taxIdOptions = [
    {
      label: 'EIN',
      value: EntityInfoNumberTypeEnum.Ein,
    },
    {
      label: 'SSN',
      value: EntityInfoNumberTypeEnum.Ssn,
    },
  ];

  useEffect(() => {
    const settings = getEditEntityInfoSettings(
      userType as BOUserTypeEnum,
      entityInfo,
    );

    setEditEntityInfoSettings(settings);
  }, [userType, entityInfo]);

  useEffect(() => {
    if (userType !== BOUserTypeEnum.International) return;

    dispatch(getAllCountries());
  }, [dispatch, userType]);

  const handleSubmit = async (values: EditEntityInfoFormValues) => {
    const {
      ein,
      ssn,
      w8w9FilePath,
      foreignTIN,
      legalCompanyName,
      incorporationCountry,
      taxIdOption,
    } = values;

    const data: EntityInformationRequestModel = {
      companyId,
      name: legalCompanyName.trim(),
      entityInfoFileKey: w8w9FilePath[0],
    };

    if (userType === BOUserTypeEnum.Company) {
      data.number = ein.replace(/\D+/g, '');
    }

    if (userType === BOUserTypeEnum.SelfEmployed) {
      if (isEinOption(toNumber(taxIdOption))) {
        data.number = ein.replace(/\D+/g, '');
        data.numberType = EntityInfoNumberTypeEnum.Ein;
      }

      if (isSsnOption(toNumber(taxIdOption))) {
        data.numberType = EntityInfoNumberTypeEnum.Ssn;
        data.number = ssn?.replace(/\D+/g, '');
      }
    }

    if (userType === BOUserTypeEnum.International) {
      data.number = foreignTIN;
      data.countryId = +incorporationCountry;
    }

    const correctAction = isAdmin
      ? updateRecruiterEntityInformation
      : updateEntityInformation;

    const result = await dispatch(correctAction(data));

    if (correctAction.fulfilled.match(result)) {
      await onEdit();

      BaseAlert.success('Entity information saved.');
      onClose();
    } else {
      BaseAlert.error(result.error.message || ApiErrorMessage);
    }
  };

  const handleTaxIdSelection = (
    e: any,
    formik: FormikProps<EditEntityInfoFormValues>,
  ) => {
    const fieldToClear = isEinOption(e.target.value) ? 'ssn' : 'ein';

    formik.setFieldTouched('taxIdOption', false, false);
    formik.setFieldValue(fieldToClear, '', false);
    formik.setFieldTouched(fieldToClear, false, false);
    formik.setStatus({
      ...formik.status,
      secureStatus: {
        ...formik.status?.secureStatus,
        [fieldToClear]: {
          firstChangeOccurred: false,
          plainTextVisible: false,
        },
      },
    });
  };

  const handleModalCancel = () => {
    if (formRef.current?.isSubmitting) return;

    if (formRef.current?.dirty) {
      return Modal.confirm({
        title: 'Are you sure? The data will not be saved.',
        icon: <span className="icon icon-attention-filled" />,
        onOk() {
          onClose();
        },
        onCancel() {},
      });
    }
    onClose();
  };

  if (!editEntityInfoSettings) return null;

  const { initialValues, validationSchema } = editEntityInfoSettings;

  return (
    <BaseModal
      title="Edit entity information"
      maxWidth={468}
      open={true}
      onCancel={handleModalCancel}
    >
      <Formik
        enableReinitialize
        innerRef={formRef}
        onSubmit={handleSubmit}
        initialValues={initialValues}
        validationSchema={validationSchema}
      >
        {(formik) => (
          <Form layout="vertical">
            <div className="modal-scroll-wrapper">
              <div className="modal-title">Entity information</div>

              <Row gutter={42}>
                {formik.values.hasOwnProperty('legalCompanyName') && (
                  <Col xs={24}>
                    <FormItem
                      label={
                        userType === BOUserTypeEnum.SelfEmployed
                          ? 'Legal name *'
                          : 'Legal company name *'
                      }
                      name="legalCompanyName"
                    >
                      <Input
                        name="legalCompanyName"
                        placeholder={
                          userType === BOUserTypeEnum.SelfEmployed
                            ? 'Enter legal name'
                            : 'Enter legal company name'
                        }
                      />
                    </FormItem>
                  </Col>
                )}

                {userType === BOUserTypeEnum.SelfEmployed && (
                  <>
                    <Col xs={24} md={12}>
                      <FormItem
                        className={cx(
                          'inline-radio radio-alignment',
                          styles.entityRadio,
                        )}
                        name="taxIdOption"
                      >
                        <span className={styles.text}>Tax ID *</span>

                        <Radio.Group
                          className="radio"
                          name="taxIdOption"
                          options={taxIdOptions}
                          onChange={(e) => handleTaxIdSelection(e, formik)}
                        />
                      </FormItem>
                    </Col>

                    {isEinOption(toNumber(formik.values.taxIdOption)) && (
                      <Col xs={24}>
                        <FormItem label="EIN *" name="ein">
                          <MaskedInput
                            name="ein"
                            mask={einMask}
                            placeholder="99-9999999"
                            secureMode={!isAdmin}
                          />
                        </FormItem>
                      </Col>
                    )}

                    {isSsnOption(toNumber(formik.values.taxIdOption)) && (
                      <Col xs={24}>
                        <FormItem label="SSN *" name="ssn">
                          <MaskedInput
                            name="ssn"
                            mask={ssnMask}
                            placeholder="999-99-9999"
                            secureMode={!isAdmin}
                          />
                        </FormItem>
                      </Col>
                    )}
                  </>
                )}

                {userType !== BOUserTypeEnum.SelfEmployed && (
                  <>
                    {formik.values.hasOwnProperty('ein') && (
                      <Col xs={24}>
                        <FormItem label="EIN *" name="ein">
                          <MaskedInput
                            name="ein"
                            mask={einMask}
                            placeholder="99-9999999"
                            secureMode={!isAdmin}
                          />
                        </FormItem>
                      </Col>
                    )}

                    {formik.values.hasOwnProperty('ssn') && (
                      <Col xs={24}>
                        <FormItem label="SSN *" name="ssn">
                          <MaskedInput
                            name="ssn"
                            mask={ssnMask}
                            placeholder="999-99-9999"
                            secureMode={!isAdmin}
                          />
                        </FormItem>
                      </Col>
                    )}
                  </>
                )}

                {formik.values.hasOwnProperty('incorporationCountry') && (
                  <>
                    <Col xs={24}>
                      <FormItem
                        label="Tax identification number *"
                        name="foreignTIN"
                      >
                        <Input
                          name="foreignTIN"
                          placeholder="Enter tax identification number"
                          secureMode={!isAdmin}
                        />
                      </FormItem>
                    </Col>

                    <Col xs={24}>
                      <FormItem
                        label="Country of incorporation *"
                        name="incorporationCountry"
                      >
                        <FormikSelectWithLoading
                          isLoading={isCountriesLoading}
                          showSearch
                          optionFilterProp="children"
                          name="incorporationCountry"
                          placeholder="Select country of incorporation"
                        >
                          {countries.map((country) => (
                            <FormikSelectWithLoading.Option
                              key={`select-country-${country.id}`}
                              value={country.id || -1}
                            >
                              {country.name}
                            </FormikSelectWithLoading.Option>
                          ))}
                        </FormikSelectWithLoading>
                      </FormItem>
                    </Col>
                  </>
                )}
              </Row>
              {formik.values.hasOwnProperty('w8w9FilePath') && (
                <>
                  <Divider className="modal-divider after-form" />

                  <div className="modal-title">
                    {userType === BOUserTypeEnum.International
                      ? 'Upload W8 *'
                      : 'Upload W9 *'}
                  </div>

                  <Row gutter={[0, 40]} justify="center">
                    <Col xs={24}>
                      <div className="upload-wrapper">
                        <W8W9Uploder
                          w8w9File={entityInfoFile}
                          isAdmin={isAdmin}
                        />
                      </div>
                    </Col>
                  </Row>
                </>
              )}
            </div>

            <BaseModalFooter>
              <Button onClick={handleModalCancel}>Cancel</Button>

              <SubmitButton>Save</SubmitButton>
            </BaseModalFooter>
          </Form>
        )}
      </Formik>
    </BaseModal>
  );
}
